import Daily, { DailyCall, DailyEventObject, DailyParticipant } from '@daily-co/daily-js';
import { createDailyRoom, deleteDailyRoom } from '../config/daily';

class DailyService {
    private callObject: DailyCall | null = null;
    private isInitialized = false;

    /**
     * Inicializa o serviço Daily.co
     */
    async initialize(): Promise<void> {
        if (this.isInitialized && this.callObject) {
            return;
        }

        try {
            this.callObject = Daily.createCallObject();
            this.isInitialized = true;
        } catch (error) {
            console.error('Erro ao inicializar Daily.co:', error);
            throw error;
        }
    }

    /**
     * Entra em uma videochamada
     */
    async joinCall(
        roomUrl: string,
        userName?: string
    ): Promise<void> {
        if (!this.callObject) {
            await this.initialize();
        }

        if (!this.callObject) {
            throw new Error('Daily.co não inicializado');
        }

        try {
            await this.callObject.join({
                url: roomUrl,
                userName: userName || 'Usuário',
            });
        } catch (error) {
            console.error('Erro ao entrar na chamada:', error);
            throw error;
        }
    }

    /**
     * Sai da videochamada
     */
    async leaveCall(): Promise<void> {
        if (this.callObject) {
            try {
                await this.callObject.leave();
            } catch (error) {
                console.error('Erro ao sair da chamada:', error);
                throw error;
            }
        }
    }

    /**
     * Destroi o call object
     */
    async destroy(): Promise<void> {
        if (this.callObject) {
            try {
                await this.callObject.destroy();
                this.callObject = null;
                this.isInitialized = false;
            } catch (error) {
                console.error('Erro ao destruir Daily:', error);
            }
        }
    }

    /**
     * Muta/desmuta áudio
     */
    async toggleAudio(enabled: boolean): Promise<void> {
        if (!this.callObject) {
            throw new Error('Call object não inicializado');
        }

        try {
            await this.callObject.setLocalAudio(enabled);
        } catch (error) {
            console.error('Erro ao alternar áudio:', error);
            throw error;
        }
    }

    /**
     * Liga/desliga vídeo
     */
    async toggleVideo(enabled: boolean): Promise<void> {
        if (!this.callObject) {
            throw new Error('Call object não inicializado');
        }

        try {
            await this.callObject.setLocalVideo(enabled);
        } catch (error) {
            console.error('Erro ao alternar vídeo:', error);
            throw error;
        }
    }

    /**
     * Registra event handlers
     */
    on(event: string, callback: (event?: DailyEventObject) => void): void {
        if (!this.callObject) {
            throw new Error('Call object não inicializado');
        }
        this.callObject.on(event as any, callback);
    }

    /**
     * Remove event handlers
     */
    off(event: string, callback: (event?: DailyEventObject) => void): void {
        if (!this.callObject) {
            return;
        }
        this.callObject.off(event as any, callback);
    }

    /**
     * Obtém participantes da chamada
     */
    participants(): { [id: string]: DailyParticipant } {
        if (!this.callObject) {
            return {};
        }
        return this.callObject.participants();
    }

    /**
     * Obtém call object
     */
    getCallObject(): DailyCall | null {
        return this.callObject;
    }

    /**
     * Verifica se está em uma chamada
     */
    isInCall(): boolean {
        return this.callObject !== null && this.callObject.meetingState() === 'joined-meeting';
    }
}

// Singleton
export const dailyService = new DailyService();
export default dailyService;
